/**
 * MapExpandModal.jsx
 * Modal para visualizacao expandida do mapa
 *
 * Design System Premium Fleet v2.0
 *
 * Uso:
 *   <MapExpandModal
 *     open={!!expandedMap}
 *     onClose={() => setExpandedMap(null)}
 *     latitude={expandedMap?.latitude}
 *     longitude={expandedMap?.longitude}
 *     title="Evento: Alarme"
 *     subtitle="10/01/2026 14:30"
 *   />
 */

import { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Button,
  Stack,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import StreetviewIcon from '@mui/icons-material/Streetview';
import DirectionsIcon from '@mui/icons-material/Directions';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { usePremiumTokens } from '../../theme/usePremiumTokens';
import MapView from '../../../map/core/MapView';
import MapMarkers from '../../../map/MapMarkers';
import MapCamera from '../../../map/MapCamera';
import MapScale from '../../../map/MapScale';
import MapGeofence from '../../../map/MapGeofence';

// ============================================================================
// CONSTANTES
// ============================================================================

const MARKER_CATEGORIES = {
  default: { color: '#00d4aa', name: 'Posição' },
  alarm: { color: '#f85149', name: 'Alarme' },
  geofenceEnter: { color: '#22c55e', name: 'Entrada' },
  geofenceExit: { color: '#f97316', name: 'Saída' },
  stop: { color: '#d29922', name: 'Parada' },
  secondary: { color: '#8b5cf6', name: 'Dispositivo 2' },
};

// ============================================================================
// COMPONENTE: CoordinatesDisplay
// ============================================================================

const CoordinatesDisplay = ({ latitude, longitude, tokens, onCopy }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 1.5,
      py: 0.75,
      backgroundColor: tokens?.surface?.glass || 'rgba(255,255,255,0.05)',
      borderRadius: tokens?.radius?.sm || '4px',
      border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
    }}
  >
    <MyLocationIcon sx={{ fontSize: 16, color: tokens?.accent?.primary || '#00d4aa' }} />
    <Typography
      variant="body2"
      sx={{
        color: tokens?.text?.secondary || '#8b949e',
        fontFamily: 'monospace',
        fontSize: '12px',
      }}
    >
      {latitude?.toFixed(6)}, {longitude?.toFixed(6)}
    </Typography>
    <Tooltip title="Copiar coordenadas">
      <IconButton size="small" onClick={onCopy}>
        <ContentCopyIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  </Box>
);

// ============================================================================
// COMPONENTE: MapControls
// ============================================================================

const MapControls = ({ onZoomIn, onZoomOut, tokens }) => (
  <Box
    sx={{
      position: 'absolute',
      top: 10,
      right: 10,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      zIndex: 10,
    }}
  >
    <IconButton
      size="small"
      onClick={onZoomIn}
      sx={{
        backgroundColor: tokens?.surface?.elevated || '#161b22',
        border: `1px solid ${tokens?.border?.subtle}`,
        '&:hover': { backgroundColor: tokens?.surface?.hover || '#1c2128' },
      }}
    >
      <ZoomInIcon sx={{ fontSize: 18 }} />
    </IconButton>
    <IconButton
      size="small"
      onClick={onZoomOut}
      sx={{
        backgroundColor: tokens?.surface?.elevated || '#161b22',
        border: `1px solid ${tokens?.border?.subtle}`,
        '&:hover': { backgroundColor: tokens?.surface?.hover || '#1c2128' },
      }}
    >
      <ZoomOutIcon sx={{ fontSize: 18 }} />
    </IconButton>
  </Box>
);

// ============================================================================
// COMPONENTE PRINCIPAL: MapExpandModal
// ============================================================================

const MapExpandModal = ({
  open,
  onClose,

  // Posicao principal
  latitude,
  longitude,

  // Posicao secundaria (correlacao)
  secondaryLatitude,
  secondaryLongitude,
  secondaryName,

  // Metadados
  title,
  subtitle,
  deviceName,
  eventType,

  // Geocerca
  geofenceId,
  geofenceName,

  // Customizacao
  markerCategory = 'default',
  maxWidth = 'md',
}) => {
  const tokens = usePremiumTokens();

  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [zoom, setZoom] = useState(16);

  // Configuracao do marcador
  const markerConfig = MARKER_CATEGORIES[markerCategory] || MARKER_CATEGORIES.default;

  // Marcadores para o mapa (MapMarkers espera { latitude, longitude, image, title })
  const markers = useMemo(() => {
    const m = [];

    if (latitude && longitude) {
      m.push({
        latitude,
        longitude,
        image: 'default-error', // Icone padrao do Traccar
        title: deviceName || '',
      });
    }

    if (secondaryLatitude && secondaryLongitude) {
      m.push({
        latitude: secondaryLatitude,
        longitude: secondaryLongitude,
        image: 'default-neutral',
        title: secondaryName || '',
      });
    }

    return m;
  }, [latitude, longitude, secondaryLatitude, secondaryLongitude, deviceName, secondaryName]);

  // Handlers
  const handleCopyCoords = useCallback(() => {
    const coords = `${latitude}, ${longitude}`;
    navigator.clipboard.writeText(coords);
    setSnackbar({ open: true, message: 'Coordenadas copiadas!' });
  }, [latitude, longitude]);

  const handleOpenGoogleMaps = useCallback(() => {
    window.open(
      `https://www.google.com/maps?q=${latitude},${longitude}`,
      '_blank'
    );
  }, [latitude, longitude]);

  const handleOpenStreetView = useCallback(() => {
    window.open(
      `https://www.google.com/maps?layer=c&cbll=${latitude},${longitude}`,
      '_blank'
    );
  }, [latitude, longitude]);

  const handleOpenDirections = useCallback(() => {
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`,
      '_blank'
    );
  }, [latitude, longitude]);

  const handleZoomIn = useCallback(() => {
    setZoom((z) => Math.min(z + 1, 20));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((z) => Math.max(z - 1, 5));
  }, []);

  if (!latitude || !longitude) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={maxWidth}
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: tokens?.surface?.elevated || '#161b22',
            borderRadius: tokens?.radius?.lg || '12px',
            border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
            overflow: 'hidden',
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            borderBottom: `1px solid ${tokens?.border?.subtle}`,
            py: 1.5,
            px: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography
                variant="h6"
                sx={{
                  color: tokens?.text?.primary || '#f0f6fc',
                  fontSize: '16px',
                  fontWeight: 600,
                }}
              >
                {title || 'Localização'}
              </Typography>

              {eventType && (
                <Chip
                  label={eventType}
                  size="small"
                  sx={{
                    backgroundColor: `${markerConfig.color}20`,
                    color: markerConfig.color,
                    fontSize: '11px',
                    height: 20,
                  }}
                />
              )}
            </Box>

            {subtitle && (
              <Typography
                variant="caption"
                sx={{ color: tokens?.text?.secondary || '#8b949e' }}
              >
                {subtitle}
              </Typography>
            )}

            {deviceName && (
              <Typography
                variant="caption"
                sx={{
                  color: tokens?.text?.muted || '#6e7681',
                  display: 'block',
                }}
              >
                {deviceName}
              </Typography>
            )}
          </Box>

          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: tokens?.text?.secondary || '#8b949e',
              '&:hover': { backgroundColor: tokens?.surface?.hover },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Mapa */}
        <DialogContent sx={{ p: 0, height: 400, position: 'relative' }}>
          <MapView>
            <MapMarkers markers={markers} showTitles />
            <MapCamera
              latitude={latitude}
              longitude={longitude}
              zoom={zoom}
            />
            <MapScale />
            {geofenceId && <MapGeofence geofenceId={geofenceId} />}
          </MapView>

          {/* Controles de zoom */}
          <MapControls
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            tokens={tokens}
          />

          {/* Badge de geocerca */}
          {geofenceName && (
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                left: 10,
                zIndex: 10,
              }}
            >
              <Chip
                label={geofenceName}
                size="small"
                sx={{
                  backgroundColor: tokens?.surface?.elevated || '#161b22',
                  color: tokens?.text?.primary,
                  border: `1px solid ${tokens?.border?.subtle}`,
                }}
              />
            </Box>
          )}

          {/* Info do secundario (correlacao) */}
          {secondaryName && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 10,
                left: 10,
                zIndex: 10,
              }}
            >
              <Chip
                icon={<MyLocationIcon sx={{ fontSize: 14, color: '#8b5cf6 !important' }} />}
                label={secondaryName}
                size="small"
                sx={{
                  backgroundColor: tokens?.surface?.elevated || '#161b22',
                  color: '#8b5cf6',
                  border: '1px solid #8b5cf6',
                }}
              />
            </Box>
          )}
        </DialogContent>

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${tokens?.border?.subtle}`,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 2,
          }}
        >
          {/* Coordenadas */}
          <CoordinatesDisplay
            latitude={latitude}
            longitude={longitude}
            tokens={tokens}
            onCopy={handleCopyCoords}
          />

          {/* Acoes */}
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Tooltip title="Ver no Street View">
              <Button
                size="small"
                variant="outlined"
                startIcon={<StreetviewIcon />}
                onClick={handleOpenStreetView}
                sx={{
                  borderColor: tokens?.border?.default,
                  color: tokens?.text?.secondary,
                  '&:hover': {
                    borderColor: tokens?.accent?.primary,
                    backgroundColor: tokens?.accent?.subtle,
                  },
                }}
              >
                Street View
              </Button>
            </Tooltip>

            <Tooltip title="Traçar rota">
              <Button
                size="small"
                variant="outlined"
                startIcon={<DirectionsIcon />}
                onClick={handleOpenDirections}
                sx={{
                  borderColor: tokens?.border?.default,
                  color: tokens?.text?.secondary,
                  '&:hover': {
                    borderColor: tokens?.accent?.primary,
                    backgroundColor: tokens?.accent?.subtle,
                  },
                }}
              >
                Rota
              </Button>
            </Tooltip>

            <Button
              size="small"
              variant="contained"
              startIcon={<OpenInNewIcon />}
              onClick={handleOpenGoogleMaps}
              sx={{
                backgroundColor: tokens?.accent?.primary || '#00d4aa',
                color: tokens?.text?.inverse || '#0d1117',
                '&:hover': {
                  backgroundColor: tokens?.accent?.hover || '#00e5bb',
                },
              }}
            >
              Google Maps
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {/* Snackbar de confirmacao */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          sx={{
            backgroundColor: tokens?.accent?.primary || '#00d4aa',
            color: tokens?.text?.inverse || '#0d1117',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MapExpandModal;
